'use client';

import { Alert, AlertDescription } from '@ahafood/ui/components/alert';
import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@ahafood/ui/components/form';
import { Input } from '@ahafood/ui/components/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type PhoneFormData, phoneSchema } from './validations';

interface PhoneLoginFormProps {
  onSuccess: (phoneNumber: string) => void;
  isLoading?: boolean;
  error?: string;
  onClearError?: () => void;
}

export function PhoneLoginForm({
  onSuccess,
  isLoading = false,
  error,
  onClearError,
}: PhoneLoginFormProps) {
  const form = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phoneNumber: '',
    },
    mode: 'onChange',
  });
  const phoneValue = form.watch('phoneNumber');
  const [isOpen, setIsOpen] = useState(false);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const [blockedUntil, setBlockedUntil] = useState<number>(() => {
    const raw = localStorage.getItem('otp_global_blocked_until');
    return raw ? parseInt(raw, 10) : 0;
  });

  const [remainingTime, setRemainingTime] = useState(() =>
    Math.max(Math.floor((blockedUntil - Date.now()) / 1000), 0)
  );

  const isBlocked = remainingTime > 0;

  useEffect(() => {
    if (!blockedUntil) return;

    const interval = setInterval(() => {
      const secondsLeft = Math.max(
        Math.floor((blockedUntil - Date.now()) / 1000),
        0
      );
      setRemainingTime(secondsLeft);

      if (secondsLeft <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [blockedUntil]);

  useEffect(() => {
    if (remainingTime <= 0 && blockedUntil) {
      localStorage.removeItem('otp_global_blocked_until');
      setBlockedUntil(0);
    }
  }, [remainingTime, blockedUntil]);

  // Format phone for display (with spaces)
  const formatPhoneDisplay = useCallback((value: string): string => {
    const numbers = value.replace(/\D/g, '');

    // Handle 84 format
    if (numbers.startsWith('84')) {
      const rest = numbers.slice(2);
      if (rest.length <= 3) return `84 ${rest}`;
      if (rest.length <= 6) return `84 ${rest.slice(0, 3)} ${rest.slice(3)}`;
      return `84 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6, 9)}`;
    }

    // Handle 0 format
    if (numbers.startsWith('0')) {
      if (numbers.length <= 4) return numbers;
      if (numbers.length <= 7)
        return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
      return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 10)}`;
    }

    // Default formatting
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6)
      return `${numbers.slice(0, 3)} ${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)} ${numbers.slice(3, 6)} ${numbers.slice(6, 9)}`;
  }, []);

  const displayValue = useMemo(
    () => formatPhoneDisplay(phoneValue),
    [phoneValue, formatPhoneDisplay]
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value.replace(/\s/g, ''); // Remove spaces
      form.setValue('phoneNumber', rawValue, { shouldValidate: true });
      onClearError?.();
    },
    [form, onClearError]
  );

  const onSubmit = useCallback(
    (data: PhoneFormData) => {
      // phoneSchema automatically transforms to 84 format
      onSuccess(data.phoneNumber);
    },
    [onSuccess]
  );

  const isFormValid = form.formState.isValid && phoneValue.length >= 10;

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormControl>
                  {/* <div className="relative"> */}
                  {/* <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-neutral-50 text-sm font-medium">
                        +
                      </span>
                    </div> */}
                  <Input
                    {...field}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9 ]*"
                    autoComplete="tel"
                    placeholder="Nhập số điện thoại của bạn"
                    className="pl-4 text-base font-semibold h-12 transition-all duration-200"
                    value={displayValue}
                    onChange={handlePhoneChange}
                    disabled={isLoading}
                    maxLength={20}
                    readOnly={isBlocked}
                    onClick={() => {
                      if (isBlocked) {
                        setIsOpen(true);
                      }
                    }}
                  />
                  {/* </div> */}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && (
            <Alert
              variant="destructive"
              className="animate-in slide-in-from-top-2"
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-primary-50 to-primary-60 hover:from-primary-60 hover:to-primary-70 transition-all duration-200 disabled:opacity-50"
            disabled={!isFormValid || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 size-5 animate-spin" />
                Đang xử lý...
              </>
            ) : (
              <>Xác nhận</>
            )}
          </Button>
        </form>
      </Form>

      <div className="flex items-center justify-center">
        <div className="flex-1 h-px bg-neutral-20" />
        <span className="px-4 text-neutral-40 text-sm">hoặc</span>
        <div className="flex-1 h-px bg-neutral-20" />
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="rounded-lg">
          <DialogTitle>Xác thực không thành công</DialogTitle>
          <span>
            Bạn đã nhập sai OTP 5 lần liên tiếp. Vì lý do bảo mật, bạn không thể
            tiếp tục đăng nhập. Vui lòng thử lại sau
            <span className="font-semibold ml-2 text-blue-50">
              {formatTime(remainingTime)}s.
            </span>
          </span>
          <Button onClick={() => setIsOpen(false)}>Tôi đã hiểu</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
